import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import {
  Table,
  Button,
  Tag,
  Space,
  Modal,
  notification,
  Spin,
  Empty,
  Tooltip,
} from "antd";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineClipboardList,
  HiOutlineChevronLeft,
  HiOutlineClock,
  HiOutlineCalendar,
} from "react-icons/hi";

import { encryptData, decryptData } from "../Crypto_Helpers/cryptoHelper";
import { initEncryptionSession } from "../../encryptionSession";

const ViewWorkingHoursComponent = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [workingHours, setWorkingHours] = useState([]);

  useEffect(() => {
    fetchWorkingHours();
  }, []);

  // ================= FETCH =================
  const fetchWorkingHours = async () => {
    setLoading(true);
    try {
      const doctorId = Cookies.get("doctor_id") || Cookies.get("Doctor_id");
      const clinicId = Cookies.get("clinic_id") || Cookies.get("Clinic_id") || 0;
      const payload = {
        doctorId: doctorId,
        clinicId: clinicId,
        limit: "100",
        pageno: "1",
      };

      const response = await axiosInstance.post(
        "doctor/getdocAppointmentSettings",
        payload
      );

      if (response.data?.status === 1 || response.data?.ErrorCode === "9999") {
        let details = response.data.data?.[0]?.details || response.data.data || [];
        if (typeof details === 'string') {
          const aesKey = await initEncryptionSession();
          details = await decryptData(details, aesKey);
        }

        const items = Array.isArray(details) ? details : (details?.details || []);
        if (Array.isArray(items)) {
          const formatted = items.map((item) => ({
            ...item,
            key: item.id,
            days: formatDays(item.days || item.day),
          }));
          setWorkingHours(formatted);
        }
      }
    } catch (err) {
      console.error("Failed to fetch working hours", err);
      notification.error({ message: "Failed to load working hours" });
    } finally {
      setLoading(false);
    }
  };

  // ================= FORMAT DAYS =================
  const formatDays = (dayString) => {
    if (!dayString) return "-";
    const dayArray = typeof dayString === 'string' ? dayString.split(',') : dayString;

    if (dayArray.length === 7) return "All Days";

    const daysMap = {
      1: "Sun",
      2: "Mon",
      3: "Tue",
      4: "Wed",
      5: "Thu",
      6: "Fri",
      7: "Sat",
    };

    return dayArray.map((d) => daysMap[String(d).trim()] || d).join(", ");
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    Modal.confirm({
      title: "Confirm Deletion",
      icon: <HiOutlineTrash className="text-red-500" size={22} />,
      content: "Are you sure you want to delete these working hours?",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await axiosInstance.post(
            "doctor/deletedocAppointmentSettings",
            { id }
          );

          if (response.data.status === 1) {
            notification.success({
              message: "Working hours deleted successfully",
            });
            fetchWorkingHours();
          } else {
            notification.error({
              message: response.data.msg || "Delete failed",
            });
          }
        } catch (err) {
          console.error("Delete error", err);
          notification.error({ message: "Delete failed" });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: "Specialty",
      dataIndex: "speciality_name",
      key: "speciality_name",
      render: (text) => <span className="text-[11px] text-gray-500 font-bold uppercase tracking-wider">{text || "—"}</span>
    },
    {
      title: "Service",
      dataIndex: "service_name",
      key: "service_name",
      render: (text) => <span className="font-bold text-gray-800 text-xs">{text || "—"}</span>
    },
    {
      title: "Slot Duration",
      dataIndex: "slot_duration",
      key: "slot_duration",
      render: (text) => (
        <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-bold px-2 py-0.5 rounded">
          {text} mins
        </span>
      ),
    },
    {
      title: "Appointment Type",
      dataIndex: "appointment_type",
      key: "appointment_type",
      render: (type) => (
        <span className="inline-block bg-gray-100 text-gray-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
          {type || "General"}
        </span>
      )
    },
    {
      title: "No. of Slots",
      dataIndex: "no_of_slots",
      key: "no_of_slots",
      render: (count) => (
        <span className="inline-block bg-teal-50 text-teal-700 border border-teal-200 text-xs font-black px-2 py-0.5 rounded">
          {count || 0}
        </span>
      )
    },
    {
      title: "Working Days",
      dataIndex: "days",
      key: "days",
      render: (days) => <span className="text-gray-600 text-xs font-semibold">{days}</span>
    },
    {
      title: "Working Hours",
      key: "working_hours",
      render: (_, record) => (
        <span className="text-teal-700 font-bold text-xs whitespace-nowrap">
          {record.from_time} - {record.to_time}
        </span>
      ),
    },
    {
      title: "Actions",
      align: "right",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit Schedule">
            <button
              onClick={() => history.push(`/dashboard/workinghours/editworkinghours/${record.id}`)}
              className="p-1 hover:bg-blue-50 rounded text-blue-600 transition-colors border-none bg-transparent cursor-pointer"
            >
              <HiOutlinePencil size={16} />
            </button>
          </Tooltip>

          <Tooltip title="Delete Schedule">
            <button
              onClick={() => handleDelete(record.id)}
              className="p-1 hover:bg-red-50 rounded text-red-600 transition-colors border-none bg-transparent cursor-pointer"
            >
              <HiOutlineTrash size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-2.5 animate-in fade-in duration-300 w-full text-gray-800">
      {/* HEADER CARD - COMPACT */}
      <div className="bg-white px-4 py-2.5 rounded-lg border border-gray-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => history.goBack()}
            className="p-1 hover:bg-gray-100 rounded text-gray-500 transition-colors cursor-pointer shrink-0"
          >
            <HiOutlineChevronLeft size={18} />
          </button>

          <div>
            <h2 className="text-sm font-bold text-gray-800 leading-tight m-0">
              Manage Working Hours
            </h2>
            <p className="text-[11px] text-gray-400 m-0 font-medium">
              Configure and view your clinical availability schedule
            </p>
          </div>
        </div>

        <button
          onClick={() => history.push("/dashboard/workinghours/addservice")}
          className="flex items-center gap-1.5 bg-[#009688] hover:bg-[#00897b] text-white px-3.5 py-1.5 rounded-lg font-bold text-xs transition-all shadow-2xs border-none cursor-pointer w-full sm:w-auto justify-center sm:justify-start"
        >
          <HiOutlinePlus size={15} />
          <span>Add Working Hours</span>
        </button>
      </div>

      {/* TABLE CARD - COMPACT */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-2xs overflow-hidden">
        {loading ? (
          <div className="py-10 flex flex-col items-center justify-center gap-2">
            <Spin size="small" />
            <p className="text-gray-400 font-medium text-xs">Loading Working Hours...</p>
          </div>
        ) : workingHours.length > 0 ? (
          <Table
            columns={columns}
            dataSource={workingHours}
            rowKey="id"
            size="small"
            scroll={{ x: 850 }}
            pagination={{
              pageSize: 10,
              position: ['bottomRight'],
              className: "px-3 py-2 m-0"
            }}
            className="working-hours-compact-table"
          />
        ) : (
          <div className="py-12 text-center bg-gray-50/20">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div className="max-w-xs mx-auto">
                  <p className="text-gray-800 font-bold text-xs m-0">No Working Hours Defined</p>
                  <p className="text-gray-400 text-[11px] mt-0.5 mb-3">Define your first working hour schedule to enable patient bookings.</p>
                  <Button
                    type="primary"
                    className="bg-[#009688] hover:bg-[#00897b] border-none rounded-lg h-8 px-4 font-bold text-xs"
                    onClick={() => history.push("/dashboard/workinghours/addservice")}
                  >
                    Add Working Hours
                  </Button>
                </div>
              }
            />
          </div>
        )}
      </div>

      <style jsx="true">{`
        .working-hours-compact-table .ant-table-thead > tr > th {
          background: #fafafa !important;
          color: #6b7280 !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          padding: 8px 12px !important;
          border-bottom: 1px solid #f3f4f6 !important;
        }
        .working-hours-compact-table .ant-table-tbody > tr > td {
          padding: 7px 12px !important;
          border-bottom: 1px solid #f9fafb !important;
        }
        .working-hours-compact-table .ant-table-row:hover > td {
          background-color: #f9fafb !important;
        }
      `}</style>
    </div>
  );
};

export default ViewWorkingHoursComponent;
